import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusPill from '../components/StatusPill';
import { Chevron, Cross, Folder, Plus, Tick } from '../components/AppIcons';
import { Brand } from '../../components/Brand';
import { useActiveCompany } from '../state';
import { createEmployee, type ContractType } from '../../lib/entities/employees';
import { getEntityQuestions, answerEntityQuestion } from '../../lib/entities/entityQuestions';
import type { OnboardingQuestion } from '../../lib/entities/onboarding';
import PredicateQuestionLoop from '../components/PredicateQuestionLoop';
import type { Employee } from '../types';
import '../../styles/app-employees.css';

/* ---------------------------------------------------------- Contract types */

/** Display labels shown in the form; mapped to the backend enum on submit. */
const CONTRACT_TYPES = ['Full-time', 'Part-time', 'Casual'];

const CONTRACT_TYPE_TO_DTO: Record<string, ContractType> = {
  'Full-time': 'fullTime',
  'Part-time': 'partTime',
  Casual: 'casual',
};

/** Two-letter monogram for the avatar, e.g. "Priya Shah" → "PS". */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase())
    .slice(0, 2)
    .join('');
}

/* ---------------------------------------------------------- Employee card */

function EmployeeCard({ employee, isNew }: { employee: Employee; isNew: boolean }) {
  const { cases } = useActiveCompany();
  // The person's own resolved checklist: what their predicate answers make required, and how much is
  // still outstanding. Driven entirely by the backend resolution, not a stored field.
  const { needed, outstanding, needsAnswer } = employee.checklist;
  const openCases = cases.filter(
    (c) => c.employeeId === employee.id && c.status !== 'resolved',
  ).length;

  return (
    <Link
      to={`/app/employees/${employee.id}`}
      className={`acard emp-card${isNew ? ' emp-card--new' : ''}`}
    >
      <span className="emp-card__avatar">{initials(employee.name)}</span>
      <span className="emp-card__body">
        <span className="emp-card__name">{employee.name}</span>
        <span className="emp-card__role">{employee.role}</span>
        <span className="emp-card__meta">
          {employee.contractType} · {employee.started}
        </span>
        {employee.note && <span className="emp-note">{employee.note}</span>}
        <span className="emp-card__pills">
          {/* Every employee holds the same document set; the card reflects their compliance over the
              ones that resolved to a required tier. Unanswered questions block resolution, so they take
              priority: the card flags them, and opening the person's file forces the answers. */}
          {needsAnswer > 0 ? (
            <StatusPill variant="warn">Needs answers</StatusPill>
          ) : outstanding === 0 ? (
            <StatusPill variant="ok">{needed === 0 ? 'Nothing required' : 'All in place'}</StatusPill>
          ) : (
            <StatusPill variant="warn">
              {outstanding} {outstanding === 1 ? 'document' : 'documents'} outstanding
            </StatusPill>
          )}
          {openCases > 0 && (
            <StatusPill variant="indigo" dot={false}>
              <Folder size={12} />
              {openCases} open {openCases === 1 ? 'case' : 'cases'}
            </StatusPill>
          )}
        </span>
      </span>
      <Chevron size={16} className="emp-card__chev" />
    </Link>
  );
}

/* -------------------------------------------------------------- List page */

export default function EmployeesPage() {
  const { companyId, employees, companyLoading, refetchEmployees } = useActiveCompany();
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState<{ name: string; id: string } | null>(null);
  const [newId, setNewId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [contractType, setContractType] = useState(CONTRACT_TYPES[0]);
  const [start, setStart] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // After create, the modal transitions in-place to the per-hire predicate question loop (if any apply).
  const [phase, setPhase] = useState<'form' | 'questions'>('form');
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);
  const [firstQuestion, setFirstQuestion] = useState<OnboardingQuestion | null>(null);

  const toastTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  // Holds the latest close behaviour so the (stable) Escape handler always calls the current one.
  const closeRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  const count = employees.length;
  const isLoading = companyLoading && count === 0;

  const openModal = () => {
    setName('');
    setRole('');
    setContractType(CONTRACT_TYPES[0]);
    setStart('');
    setFormError(null);
    setPhase('form');
    setCreated(null);
    setFirstQuestion(null);
    setModalOpen(true);
  };

  // Finish: refresh the list, flag the new row, toast, close. Called only once every applicable per-hire
  // question has been answered (or immediately when there are none).
  const finish = async (id: string, displayName: string) => {
    await refetchEmployees();
    setNewId(id);
    setToast({ name: displayName.split(' ')[0], id });
    setModalOpen(false);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  };

  // Dismissal is allowed ONLY on the form (nothing created yet). Once the predicate questions are showing,
  // the employee exists and every question MUST be answered yes/no — an unanswered predicate resolves as
  // false and shows the wrong tier — so there is no cancel, Escape, or backdrop close out of them.
  const closeModal = () => {
    if (phase === 'questions') return;
    setModalOpen(false);
  };
  closeRef.current = closeModal;

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const fullName = name.trim();
    if (!fullName || !companyId) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const employee = await createEmployee({
        companyId,
        name: fullName,
        role: role.trim() || 'Team member',
        contractType: CONTRACT_TYPE_TO_DTO[contractType] ?? 'fullTime',
        // The <input type="date"> already yields a valid "yyyy-MM-dd".
        startedOn: start,
      });
      // Any per-hire questions to ask? Pre-fetch so the modal only transitions when there's one to show.
      const next = await getEntityQuestions('/api/employees', employee.id);
      if (!next.done && next.question) {
        setCreated({ id: employee.id, name: employee.name });
        setFirstQuestion(next.question);
        setPhase('questions');
      } else {
        await finish(employee.id, employee.name);
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not add this person. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="apage">
      <PageHeader
        eyebrow="Employees"
        title="Your team"
        lead={
          <>
            {count} {count === 1 ? 'person' : 'people'} on the books.
          </>
        }
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={openModal}
            disabled={!companyId}
          >
            <Plus size={16} />
            Add an employee
          </button>
        }
      />

      {toast && (
        <div className="emp-toast" role="status">
          <span className="emp-toast__icon">
            <Tick size={12} color="#fff" />
          </span>
          <span>
            {toast.name} is on the team, and their requirements are ready to work through.{' '}
            <Link to={`/app/employees/${toast.id}`}>Open their file</Link> to make a start.
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="acard">
          <div className="empty">Loading your team…</div>
        </div>
      ) : count === 0 ? (
        <div className="acard">
          <div className="empty">
            No one on the books yet. Add your first employee to see what they need.
          </div>
        </div>
      ) : (
        <div className="emp-grid">
          {employees.map((employee) => (
            <EmployeeCard key={employee.id} employee={employee} isNew={employee.id === newId} />
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="modal-scrim" onClick={closeModal} role="presentation">
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="emp-add-title"
            onClick={(e) => e.stopPropagation()}
          >
            {/* No close control during the questions phase: the answers are required, not optional. */}
            {phase === 'form' && (
              <button type="button" className="modal__close" aria-label="Close" onClick={closeModal}>
                <Cross size={18} />
              </button>
            )}
            {phase === 'form' ? (
              <>
                <h2 className="modal__title" id="emp-add-title">
                  Add an employee
                </h2>
                <p className="emp-modal__sub">
                  Just the basics. <Brand /> builds their requirements from what UK law expects for a
                  business like yours.
                </p>
                <form className="emp-form" onSubmit={handleAdd}>
              <label className="field">
                <span className="field__label">Full name</span>
                <input
                  className="input"
                  required
                  placeholder="e.g. Alex Morgan"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field__label">Role</span>
                <input
                  className="input"
                  placeholder="e.g. Front of house"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                />
              </label>
              <div className="emp-form__row">
                <label className="field">
                  <span className="field__label">Contract type</span>
                  <select
                    className="select"
                    value={contractType}
                    onChange={(e) => setContractType(e.target.value)}
                  >
                    {CONTRACT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field__label">Start date</span>
                  <input
                    className="input"
                    type="date"
                    required
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                  />
                </label>
              </div>
              {formError && (
                <p style={{ margin: 0, color: 'var(--wine)', fontSize: '13.5px' }} role="alert">
                  {formError}
                </p>
              )}
              <div className="emp-modal__foot">
                <button type="button" className="btn btn-secondary btn-xs" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-xs" disabled={submitting}>
                  {submitting ? 'Adding…' : 'Add to the team'}
                </button>
              </div>
                </form>
              </>
            ) : (
              created &&
              firstQuestion && (
                <PredicateQuestionLoop
                  lead={
                    <>
                      <strong>{created.name.split(' ')[0]}</strong> is on the team. These answers set which
                      requirements apply, so each one needs a yes or no.
                    </>
                  }
                  initialQuestion={firstQuestion}
                  submitAnswer={(key, value) =>
                    answerEntityQuestion('/api/employees', created.id, key, value)
                  }
                  onDone={() => finish(created.id, created.name)}
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
